import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import Joyride, { ACTIONS, EVENTS, STATUS, type CallBackProps, type Step, type Styles } from 'react-joyride';
import type { Customer, EFile } from '@tabservice/tabfile-client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { pathOfRoute, type TourId, type TourStep } from './types';
import { customerTourSteps } from './steps.customer';
import { adminTourSteps } from './steps.admin';
import { useTheme, type ResolvedTheme } from '@/lib/theme';
import {
  TourContext,
  hasSeenTourPromptLocally,
  markTourPromptSeen,
  useTour,
  type TourApi,
} from './use-tour';

/**
 * Joyride writes inline styles, so the tokens have to be handed over as values.
 * One set per theme, picked by the provider from the resolved theme.
 */
const INTER = 'Inter, ui-sans-serif, system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

function buildTourStyles(theme: ResolvedTheme): Partial<Styles> {
  const dark = theme === 'dark';
  const accent = dark ? '#d5e9ef' : '#244f92';
  const onAccent = dark ? '#0b1526' : '#ffffff';
  const surface = dark ? '#172947' : '#ffffff';
  const ink = dark ? '#e6eef8' : '#222222';
  const quiet = dark ? '#97a8c2' : '#6b7280';

  return {
    options: {
      arrowColor: surface,
      backgroundColor: surface,
      primaryColor: accent,
      textColor: ink,
      overlayColor: dark ? 'rgb(3 8 18 / 0.7)' : 'rgb(15 30 55 / 0.45)',
      spotlightShadow: dark ? '0 16px 40px rgb(0 0 0 / 0.6)' : '0 12px 32px rgb(35 79 146 / 0.28)',
      width: 400,
      // Dialogs sit at 50 and toasts at 60, so the tour has to clear both.
      zIndex: 200,
    },
    tooltip: {
      borderRadius: 10,
      fontFamily: INTER,
      padding: 20,
      boxShadow: dark
        ? '0 16px 40px rgb(0 0 0 / 0.55), 0 2px 10px rgb(0 0 0 / 0.4)'
        : '0 12px 32px rgb(35 79 146 / 0.16), 0 2px 8px rgb(35 79 146 / 0.08)',
    },
    tooltipTitle: { fontSize: 16, fontWeight: 600, color: accent, margin: 0, textAlign: 'left' },
    tooltipContent: { fontSize: 14, lineHeight: '22px', padding: '10px 0 0', textAlign: 'left', color: ink },
    tooltipFooter: { marginTop: 14 },
    buttonNext: {
      backgroundColor: accent,
      borderRadius: 8,
      color: onAccent,
      fontFamily: INTER,
      fontSize: 14,
      fontWeight: 500,
      outline: 'none',
      padding: '10px 18px',
    },
    buttonBack: { color: accent, fontFamily: INTER, fontSize: 14, fontWeight: 500, marginRight: 8 },
    buttonSkip: { color: quiet, fontFamily: INTER, fontSize: 14 },
    buttonClose: { color: quiet, height: 12, width: 12, padding: 14 },
    spotlight: { borderRadius: 10 },
    beacon: { outline: 'none' },
    beaconInner: { backgroundColor: accent },
    beaconOuter: { backgroundColor: 'rgb(213 233 239 / 0.7)', border: `2px solid ${accent}` },
  };
}

const tourLocale = {
  back: 'Back',
  close: 'Close',
  last: 'Done',
  next: 'Next',
  skip: 'Skip the tour',
};

/** Waits for a step's target to appear after a route change. */
async function waitForTarget(selector: string, timeoutMs = 3000): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    if (document.querySelector(selector)) return true;
    if (Date.now() >= deadline) return false;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

/**
 * The e-file the customer tour opens. Accepted with errors is the richest
 * state: it has several transmissions, records with real errors and a finished
 * timeline. Anything else in the list will do if there is no such e-file.
 */
function loadedFilings(queryClient: QueryClient): EFile[] {
  const all: EFile[] = [];
  for (const [, data] of queryClient.getQueriesData<{ items?: EFile[] }>({ queryKey: ['e-files'] })) {
    if (data?.items) all.push(...data.items);
  }
  return all;
}

function pickTourFilingId(queryClient: QueryClient): string | null {
  const all = loadedFilings(queryClient);
  const withErrors = all.find((filing) => filing.status === 'accepted_with_errors');
  return withErrors?.id ?? all[0]?.id ?? null;
}

/**
 * An e-file the IRS rejected, so the replacement step opens a page that can
 * actually do something. There may not be one, and the tour copes.
 */
function pickRejectedFilingId(queryClient: QueryClient): string | null {
  const all = loadedFilings(queryClient);
  return all.find((filing) => (filing.actions ?? []).includes('replace'))?.id ?? null;
}

function pickTourCustomerId(queryClient: QueryClient): string | null {
  for (const [, data] of queryClient.getQueriesData<{ items?: Customer[] }>({ queryKey: ['admin-customers'] })) {
    const first = data?.items?.[0];
    if (first) return first.id;
  }
  return null;
}

type NavigateArg = Parameters<ReturnType<typeof useNavigate>>[0];

export function TourProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const { resolved: theme } = useTheme();
  const tourStyles = useMemo(() => buildTourStyles(theme), [theme]);

  const [steps, setSteps] = useState<TourStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);
  const [tourId, setTourId] = useState<TourId | null>(null);

  // The location the moving code reads, so a callback never sees a stale path.
  const whereRef = useRef({ pathname, searchStr });
  whereRef.current = { pathname, searchStr };

  // Bumped whenever a tour starts or ends, so a pending navigation from the
  // previous tour cannot show a step that is no longer wanted.
  const runToken = useRef(0);

  const finish = useCallback(() => {
    runToken.current += 1;
    setRun(false);
    setStepIndex(0);
    setTourId(null);
  }, []);

  const goToStep = useCallback(
    async (list: TourStep[], index: number) => {
      const step = list[index];
      if (!step) {
        finish();
        return;
      }
      runToken.current += 1;
      const token = runToken.current;

      // Pause while the app moves. Joyride cannot spotlight what is not there.
      setRun(false);

      if (step.route) {
        const wantedPath = pathOfRoute(step.route);
        const wantedSearch = new URLSearchParams(step.route.search ?? {}).toString();
        const currentSearch = whereRef.current.searchStr.replace(/^\?/, '');
        if (whereRef.current.pathname !== wantedPath || currentSearch !== wantedSearch) {
          await navigate({
            to: step.route.to,
            params: step.route.params ?? {},
            search: step.route.search ?? {},
          } as unknown as NavigateArg);
        }
      }

      await waitForTarget(String(step.target));
      if (runToken.current !== token) return;

      setStepIndex(index);
      setRun(true);
    },
    [finish, navigate],
  );

  const start = useCallback(
    (id: TourId) => {
      const list =
        id === 'admin'
          ? adminTourSteps({ customerId: pickTourCustomerId(queryClient) })
          : customerTourSteps({
              filingId: pickTourFilingId(queryClient),
              rejectedFilingId: pickRejectedFilingId(queryClient),
            });
      markTourPromptSeen();
      setSteps(list);
      setTourId(id);
      setStepIndex(0);
      void goToStep(list, 0);
    },
    [goToStep, queryClient],
  );

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        finish();
        return;
      }
      if (action === ACTIONS.CLOSE) {
        finish();
        return;
      }
      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        void goToStep(steps, index + (action === ACTIONS.PREV ? -1 : 1));
      }
    },
    [finish, goToStep, steps],
  );

  const api = useMemo<TourApi>(
    () => ({ start, stop: finish, running: run, tourId }),
    [finish, run, start, tourId],
  );

  // Joyride validates the props it knows about, so the route hint comes off first.
  const joyrideSteps = useMemo<Step[]>(() => steps.map(({ route: _route, ...rest }) => rest), [steps]);

  return (
    <TourContext.Provider value={api}>
      {children}
      {joyrideSteps.length > 0 ? (
        <Joyride
          steps={joyrideSteps}
          run={run}
          stepIndex={stepIndex}
          continuous
          showProgress
          showSkipButton
          disableScrolling={false}
          spotlightClicks
          scrollOffset={96}
          callback={handleCallback}
          locale={tourLocale}
          styles={tourStyles}
        />
      ) : null}
    </TourContext.Provider>
  );
}

// StrictMode runs effects twice in development. The prompt is a one time thing.
let promptShown = false;

/**
 * The nudge a brand new person gets, once in their life.
 *
 * `seen` comes from their own user record, so a second browser, a private window or another
 * device does not ask again. It is marked as seen the moment it is SHOWN: taking the tour,
 * dismissing it and letting it time out are all answers, and only counting a dismissal is
 * what made this come back over and over.
 */
export function TourFirstVisitPrompt({ staff, seen }: { staff: boolean; seen: boolean }) {
  const tour = useTour();
  const toast = useToast();

  useEffect(() => {
    if (promptShown || seen || hasSeenTourPromptLocally()) return;
    promptShown = true;
    void markTourPromptSeen();
    const id: TourId = staff ? 'admin' : 'customer';
    toast.show({
      title: 'Welcome. Would you like a quick tour?',
      description: staff
        ? 'It covers customers, their keys and users, and switching an account to live e-file.'
        : 'It covers submitting an e-file and reading the IRS response.',
      duration: 12_000,
      onDismiss: () => {},
      action: (
        <Button size="sm" onClick={() => tour.start(id)}>
          Start tour
        </Button>
      ),
    });
  }, [seen, staff, toast, tour]);

  return null;
}

/** Test hook: lets a suite pretend the prompt has not been shown yet. */
export function resetFirstVisitPrompt(): void {
  promptShown = false;
}
